/* eslint-disable */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, X, Upload, Star, StarOff, Eye, EyeOff, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminApi, productsApi } from '../lib/api';
import { filesToCompressedDataUrls } from '../lib/imageUpload';
import StarRating from '../components/Reviews/StarRating';
import BannersTab from '../components/Admin/BannersTab';
import BestSellersTab from '../components/Admin/BestSellersTab';
import WhatsAppTab from '../components/Admin/WhatsAppTab';
import AnnouncementTab from '../components/Admin/AnnouncementTab';
import UsersTab from '../components/Admin/UsersTab';
import CouponsTab from '../components/Admin/CouponsTab';

const EMPTY = {
  id: '', name: '', shade: '', category: 'matte', price: 0,
  image: '', images: [], swatch: '#A6293A', description: '',
  short_description: '', ingredients: '', weight: '', stock: 0, sku: '',
};

const Admin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState('all'); // all | approved | hidden | featured
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const loadReviews = useCallback(async () => {
    try {
      const status = reviewFilter === 'all' ? undefined : reviewFilter;
      setReviews(await adminApi.listReviews(status));
    } catch (e) {
      setReviews([]);
    }
  }, [reviewFilter]);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.is_admin) return;
    loadAll();
  }, [user, loading]);

  useEffect(() => {
    if (user?.is_admin && tab === 'reviews') loadReviews();
  }, [user, tab, loadReviews]);

  const loadAll = async () => {
    try {
      const [ps, os] = await Promise.all([adminApi.listProducts(), adminApi.listOrders()]);
      setProducts(ps);
      setOrders(os);
    } catch (e) {
      setErr('Failed to load admin data');
    }
  };

  if (loading) return <main className="bg-[#f5f1e6] min-h-screen px-6 py-20 text-center text-[#7a6a5a]">Loading…</main>;

  if (!user) {
    return (
      <main className="bg-[#f5f1e6] min-h-screen flex items-center justify-center px-6 py-20 text-center">
        <div>
          <h1 className="font-serif text-3xl italic">Sign in required.</h1>
          <Link to="/login" className="mt-6 inline-block bg-[#1a1410] text-[#f5f1e6] px-8 py-4 text-[11px] tracking-[0.32em]">SIGN IN</Link>
        </div>
      </main>
    );
  }

  if (!user.is_admin) {
    return (
      <main className="bg-[#f5f1e6] min-h-screen flex items-center justify-center px-6 py-20 text-center">
        <div>
          <h1 className="font-serif text-3xl italic">The atelier is private.</h1>
          <p className="mt-3 text-[#5a4a3e]">This area is reserved for the maison.</p>
          <button onClick={() => navigate('/')} className="mt-6 text-[11px] tracking-[0.28em] border-b border-[#1a1410] pb-1">RETURN TO MAISON</button>
        </div>
      </main>
    );
  }

  const openNew = () => { setEditing({ ...EMPTY }); setShowForm(true); };
  const openEdit = async (p) => {
    // Fetch full product (list endpoint strips images[] to save bandwidth)
    try {
      const full = await productsApi.get(p.id);
      setEditing({ ...full, images: full.images || (full.image ? [full.image] : []) });
    } catch {
      setEditing({ ...p, images: p.image ? [p.image] : [] });
    }
    setShowForm(true);
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setBusy(true); setErr('');
    try {
      const dataUrls = await filesToCompressedDataUrls(files);
      setEditing((curr) => {
        const newImages = [...(curr.images || []), ...dataUrls];
        return {
          ...curr,
          images: newImages,
          image: curr.image || newImages[0] || '',
        };
      });
    } catch (ex) {
      setErr(ex.message || 'Upload failed');
    } finally {
      setBusy(false);
      e.target.value = ''; // reset input
    }
  };

  const removeImage = (idx) => {
    setEditing((curr) => {
      const newImages = (curr.images || []).filter((_, i) => i !== idx);
      const removed = (curr.images || [])[idx];
      const newPrimary = curr.image === removed ? (newImages[0] || '') : curr.image;
      return { ...curr, images: newImages, image: newPrimary };
    });
  };

  const setPrimaryImage = (idx) => {
    setEditing((curr) => {
      const list = curr.images || [];
      if (!list[idx]) return curr;
      return { ...curr, image: list[idx] };
    });
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    // Ensure at least one image (uploaded or URL)
    const hasImages = (editing.images && editing.images.length > 0) || !!editing.image;
    if (!hasImages) {
      setErr('Please upload at least one image or paste a URL.');
      return;
    }
    setBusy(true); setErr('');
    try {
      // Make sure primary is in images list
      const finalImages = editing.images && editing.images.length > 0
        ? editing.images
        : (editing.image ? [editing.image] : []);
      const finalPrimary = editing.image || finalImages[0] || '';
      const payload = { ...editing, image: finalPrimary, images: finalImages };
      if (editing.id && products.some((p) => p.id === editing.id)) {
        await adminApi.updateProduct(editing.id, payload);
      } else {
        await adminApi.createProduct(payload);
      }
      setShowForm(false);
      await loadAll();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this shade?')) return;
    await adminApi.deleteProduct(id);
    await loadAll();
  };

  const updateOrderStatus = async (id, status) => {
    await adminApi.updateOrderStatus(id, status);
    await loadAll();
  };

  const setReviewStatus = async (id, status) => {
    await adminApi.updateReview(id, { status });
    await loadReviews();
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    await adminApi.deleteReview(id);
    await loadReviews();
  };

  const saveEditedReview = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await adminApi.updateReview(editingReview.id, {
        title: editingReview.title,
        text: editingReview.text,
        rating: editingReview.rating,
      });
      setEditingReview(null);
      await loadReviews();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <main className="bg-[#f5f1e6] min-h-screen text-[#1a1410] px-6 lg:px-10 py-16">
      <div className="max-w-7xl mx-auto">
        <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block">THE ATELIER</div>
        <h1 className="font-serif text-[56px] leading-[1.05] mt-6">Admin <span className="italic">studio.</span></h1>

        <div className="mt-10 flex gap-8 border-b border-[#e5dfd0]">
          {[['products', `Products (${products.length})`], ['orders', `Orders (${orders.length})`], ['reviews', `Reviews`], ['users', `Users`], ['coupons', `Coupons`], ['banners', `Banners`], ['best-sellers', `Best Sellers`], ['whatsapp', `WhatsApp`], ['announcement', `Announcement`]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`pb-3 text-[11px] tracking-[0.22em] transition-colors ${
                tab === k ? 'text-[#1a1410] border-b-2 border-[#1a1410]' : 'text-[#7a6a5a] hover:text-[#1a1410]'
              }`}>
              {label.toUpperCase()}
            </button>
          ))}
        </div>

        {err && <div className="mt-6 p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}

        {tab === 'products' && (
          <div className="mt-8">
            <div className="flex justify-end mb-6">
              <button onClick={openNew} className="inline-flex items-center gap-2 bg-[#1a1410] text-[#f5f1e6] px-6 py-3 text-[11px] tracking-[0.32em] hover:bg-black">
                <Plus className="w-4 h-4" strokeWidth={1.6} /> NEW SHADE
              </button>
            </div>

            <div className="overflow-x-auto border border-[#e5dfd0]">
              <table className="w-full text-sm">
                <thead className="bg-[#ece4d2] text-left text-[10px] tracking-[0.2em] text-[#3a2e26]">
                  <tr>
                    <th className="p-4">IMAGE</th>
                    <th className="p-4">NAME / SHADE</th>
                    <th className="p-4">SKU</th>
                    <th className="p-4">CATEGORY</th>
                    <th className="p-4">PRICE</th>
                    <th className="p-4">STOCK</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-[#e5dfd0]">
                      <td className="p-4"><img src={p.image} alt={p.name} className="w-14 h-16 object-cover" /></td>
                      <td className="p-4">
                        <div className="font-serif text-lg">{p.name}</div>
                        <div className="text-xs text-[#7a6a5a]">{p.shade}</div>
                      </td>
                      <td className="p-4 font-mono text-xs text-[#3a2e26]" data-testid={`admin-sku-${p.id}`}>{p.sku || <span className="text-[#b0a496]">—</span>}</td>
                      <td className="p-4 uppercase text-[11px] tracking-[0.18em]">{p.category}</td>
                      <td className="p-4 font-serif">Rs. {p.price.toFixed(2)}</td>
                      <td className="p-4">{p.stock}</td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(p)} className="inline-flex items-center gap-1 text-[11px] tracking-[0.2em] mr-4 hover:text-black">
                          <Pencil className="w-3.5 h-3.5" strokeWidth={1.4} /> EDIT
                        </button>
                        <button onClick={() => deleteProduct(p.id)} className="inline-flex items-center gap-1 text-[11px] tracking-[0.2em] text-[#7a3a2a] hover:text-[#5a1a0a]">
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.4} /> DELETE
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'orders' && (
          <div className="mt-8 overflow-x-auto border border-[#e5dfd0]">
            <table className="w-full text-sm">
              <thead className="bg-[#ece4d2] text-left text-[10px] tracking-[0.2em] text-[#3a2e26]">
                <tr>
                  <th className="p-4">ORDER</th>
                  <th className="p-4">DATE</th>
                  <th className="p-4">CUSTOMER</th>
                  <th className="p-4">ITEMS</th>
                  <th className="p-4">TOTAL</th>
                  <th className="p-4">PAYMENT</th>
                  <th className="p-4">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-[#7a6a5a]">No orders yet.</td></tr>
                ) : orders.map((o) => (
                  <tr key={o.id} className="border-t border-[#e5dfd0] align-top">
                    <td className="p-4 font-mono text-xs">{o.id.slice(0, 8).toUpperCase()}</td>
                    <td className="p-4 text-xs text-[#5a4a3e]">{new Date(o.created_at).toLocaleString()}</td>
                    <td className="p-4">
                      <div>{o.shipping?.full_name}</div>
                      <div className="text-xs text-[#7a6a5a]">{o.shipping?.city}, {o.shipping?.country}</div>
                    </td>
                    <td className="p-4">
                      <ul className="text-xs space-y-1">
                        {o.items.map((it, idx) => {
                          const skuValue = it.sku || products.find((p) => p.id === it.product_id)?.sku || '';
                          return (
                            <li key={idx} data-testid={`order-item-${o.id}-${idx}`}>
                              <span>{it.quantity} × {it.name}</span>
                              {skuValue ? (
                                <span className="ml-2 font-mono text-[10px] px-1.5 py-0.5 bg-[#ece4d2] border border-[#cdc2b1] text-[#3a2e26]" data-testid={`order-sku-${o.id}-${idx}`}>
                                  SKU: {skuValue}
                                </span>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    </td>
                    <td className="p-4 font-medium tabular-nums">Rs. {o.total.toFixed(2)}</td>
                    <td className="p-4">
                      {(() => {
                        const isOnline = o.payment?.provider === 'razorpay' || o.payment?.provider === 'online';
                        return (
                          <span
                            data-testid={`order-payment-${o.id}`}
                            className={`inline-block px-2 py-1 text-[10px] tracking-[0.18em] uppercase border ${
                              isOnline
                                ? 'bg-[#e6efe1] border-[#b7ceac] text-[#3d5a2a]'
                                : 'bg-[#f3e9d8] border-[#d8c69a] text-[#7a5a1a]'
                            }`}
                          >
                            {isOnline ? 'Online' : 'Cash on Delivery'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="p-4">
                      <select value={o.status} onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                        className="bg-transparent border border-[#cdc2b1] px-2 py-1 text-xs tracking-[0.15em] uppercase focus:outline-none focus:border-[#1a1410]">
                        <option value="confirmed">Confirmed</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'reviews' && (
          <div className="mt-8">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">FILTER</span>
              {['all', 'approved', 'featured', 'hidden', 'pending'].map((f) => (
                <button key={f} onClick={() => setReviewFilter(f)}
                  className={`text-[11px] tracking-[0.22em] uppercase px-3 py-1 border ${reviewFilter === f ? 'border-[#1a1410] bg-[#1a1410] text-[#f5f1e6]' : 'border-[#cdc2b1] hover:border-[#1a1410]'}`}>
                  {f}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto border border-[#e5dfd0]">
              <table className="w-full text-sm">
                <thead className="bg-[#ece4d2] text-left text-[10px] tracking-[0.2em] text-[#3a2e26]">
                  <tr>
                    <th className="p-4">PRODUCT</th>
                    <th className="p-4">RATING / TITLE</th>
                    <th className="p-4">REVIEW</th>
                    <th className="p-4">CUSTOMER</th>
                    <th className="p-4">STATUS</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-[#7a6a5a]">No reviews to show.</td></tr>
                  ) : reviews.map((r) => (
                    <tr key={r.id} className="border-t border-[#e5dfd0] align-top">
                      <td className="p-4">
                        {r.product ? (
                          <div className="flex items-center gap-3">
                            {r.product.image && <img src={r.product.image} alt={r.product.name} className="w-12 h-14 object-cover" />}
                            <Link to={`/product/${r.product.id}`} className="font-serif text-base hover:underline">
                              {r.product.name}
                            </Link>
                          </div>
                        ) : <span className="text-xs text-[#7a6a5a]">—</span>}
                      </td>
                      <td className="p-4 min-w-[180px]">
                        <StarRating value={r.rating} size={14} />
                        <div className="font-serif text-base mt-1">{r.title}</div>
                        <div className="text-[10px] text-[#7a6a5a] mt-1">{r.rating.toFixed(1)} / 5 · {new Date(r.created_at).toLocaleDateString()}</div>
                      </td>
                      <td className="p-4 max-w-md">
                        <p className="text-sm line-clamp-3">{r.text}</p>
                        {r.images?.length > 0 && (
                          <div className="mt-2 flex gap-1.5">
                            {r.images.slice(0, 5).map((src, idx) => (
                              <img key={idx} src={src} alt="" className="w-10 h-10 object-cover border border-[#cdc2b1]" />
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-xs">
                        {r.user_name}
                        {r.verified_purchase && <div className="text-[10px] tracking-[0.18em] text-[#3a6a3a] mt-1">VERIFIED</div>}
                      </td>
                      <td className="p-4">
                        <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 ${
                          r.status === 'featured' ? 'bg-[#1a1410] text-[#f5f1e6]' :
                          r.status === 'hidden' ? 'bg-[#cdc2b1] text-[#3a2e26]' :
                          r.status === 'pending' ? 'bg-[#f3e3d8] text-[#7a3a2a]' :
                          'bg-[#dfecd9] text-[#3a6a3a]'
                        }`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="p-4 whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-2">
                          <button onClick={() => setEditingReview({ ...r })} title="Edit"
                            className="p-1.5 hover:text-black"><Pencil className="w-3.5 h-3.5" strokeWidth={1.4} /></button>
                          {r.status === 'featured' ? (
                            <button onClick={() => setReviewStatus(r.id, 'approved')} title="Unfeature"
                              className="p-1.5 hover:text-black"><Sparkles className="w-3.5 h-3.5" strokeWidth={1.4} fill="#1a1410" /></button>
                          ) : (
                            <button onClick={() => setReviewStatus(r.id, 'featured')} title="Feature"
                              className="p-1.5 hover:text-black"><Sparkles className="w-3.5 h-3.5" strokeWidth={1.4} /></button>
                          )}
                          {r.status === 'hidden' ? (
                            <button onClick={() => setReviewStatus(r.id, 'approved')} title="Approve"
                              className="p-1.5 hover:text-black"><Eye className="w-3.5 h-3.5" strokeWidth={1.4} /></button>
                          ) : (
                            <button onClick={() => setReviewStatus(r.id, 'hidden')} title="Hide"
                              className="p-1.5 hover:text-black"><EyeOff className="w-3.5 h-3.5" strokeWidth={1.4} /></button>
                          )}
                          <button onClick={() => deleteReview(r.id)} title="Delete"
                            className="p-1.5 text-[#7a3a2a] hover:text-[#5a1a0a]"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.4} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'users' && <UsersTab />}

        {tab === 'coupons' && <CouponsTab />}

        {tab === 'banners' && <BannersTab />}

        {tab === 'best-sellers' && <BestSellersTab />}

        {tab === 'whatsapp' && <WhatsAppTab />}

        {tab === 'announcement' && <AnnouncementTab />}

        {editingReview && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-[#f5f1e6] w-full max-w-lg">
              <div className="flex items-center justify-between px-6 h-16 border-b border-[#e5dfd0]">
                <div className="text-[11px] tracking-[0.28em]">EDIT REVIEW</div>
                <button onClick={() => setEditingReview(null)}><X className="w-5 h-5" strokeWidth={1.4} /></button>
              </div>
              <form onSubmit={saveEditedReview} className="p-6 space-y-5">
                <div>
                  <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">RATING (1.0–5.0)</label>
                  <input type="number" min="1" max="5" step="0.1" value={editingReview.rating}
                    onChange={(e) => setEditingReview({ ...editingReview, rating: parseFloat(e.target.value) || 1 })}
                    className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">TITLE</label>
                  <input value={editingReview.title} onChange={(e) => setEditingReview({ ...editingReview, title: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">REVIEW</label>
                  <textarea value={editingReview.text} onChange={(e) => setEditingReview({ ...editingReview, text: e.target.value })}
                    rows={5} className="mt-2 w-full bg-transparent border border-[#cdc2b1] focus:border-[#1a1410] p-3 text-sm focus:outline-none" />
                </div>
                {err && <div className="p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}
                <div className="flex gap-3 pt-2">
                  <button type="submit" disabled={busy} className="flex-1 bg-[#1a1410] text-[#f5f1e6] py-3 text-[11px] tracking-[0.32em] hover:bg-black disabled:opacity-50">
                    {busy ? 'SAVING…' : 'SAVE'}
                  </button>
                  <button type="button" onClick={() => setEditingReview(null)} className="px-6 py-3 text-[11px] tracking-[0.32em] border border-[#cdc2b1]">CANCEL</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showForm && editing && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-[#f5f1e6] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 h-16 border-b border-[#e5dfd0]">
                <div className="text-[11px] tracking-[0.28em]">{editing.id && products.some((p) => p.id === editing.id) ? 'EDIT SHADE' : 'NEW SHADE'}</div>
                <button onClick={() => setShowForm(false)}><X className="w-5 h-5" strokeWidth={1.4} /></button>
              </div>
              <form onSubmit={saveProduct} className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-5">
                  <Field label="NAME" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
                  <Field label="SHADE" value={editing.shade} onChange={(v) => setEditing({ ...editing, shade: v })} />
                  <div>
                    <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">CATEGORY</label>
                    <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none">
                      <option value="matte">Matte</option>
                      <option value="satin">Satin</option>
                      <option value="sheer">Sheer</option>
                      <option value="gloss">Gloss</option>
                    </select>
                  </div>
                  <Field label="PRICE" type="number" step="0.01" value={editing.price} onChange={(v) => setEditing({ ...editing, price: parseFloat(v) || 0 })} />
                  <Field label="STOCK" type="number" value={editing.stock} onChange={(v) => setEditing({ ...editing, stock: parseInt(v) || 0 })} />
                  <Field label="SWATCH HEX" value={editing.swatch} onChange={(v) => setEditing({ ...editing, swatch: v })} />
                  <Field label="SKU CODE" value={editing.sku || ''} onChange={(v) => setEditing({ ...editing, sku: v })} required={false} />
                </div>
                <Field label="IMAGE URL (optional fallback)" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} required={false} />

                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">PRODUCT IMAGES</label>
                    <label className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] cursor-pointer hover:text-black">
                      <Upload className="w-4 h-4" strokeWidth={1.4} /> UPLOAD FROM DEVICE
                      <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
                    </label>
                  </div>
                  <p className="mt-2 text-xs text-[#7a6a5a]">
                    Select one or more images from your computer. They'll be resized & saved with the product. Click the star to set the primary image.
                  </p>

                  {(editing.images || []).length > 0 ? (
                    <div className="mt-4 grid grid-cols-4 gap-3">
                      {editing.images.map((src, idx) => {
                        const isPrimary = editing.image === src;
                        return (
                          <div key={idx} className={`relative group border ${isPrimary ? 'border-[#1a1410]' : 'border-[#cdc2b1]'}`}>
                            <img src={src} alt={`Product image ${idx + 1}`} className="w-full aspect-square object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                              <button type="button" onClick={() => setPrimaryImage(idx)} title={isPrimary ? 'Primary image' : 'Set as primary'}
                                className="bg-[#f5f1e6] text-[#1a1410] text-[10px] tracking-[0.2em] px-2 py-1 inline-flex items-center gap-1">
                                {isPrimary ? <Star className="w-3 h-3" strokeWidth={1.6} fill="#1a1410" /> : <StarOff className="w-3 h-3" strokeWidth={1.6} />}
                                {isPrimary ? 'PRIMARY' : 'MAKE PRIMARY'}
                              </button>
                              <button type="button" onClick={() => removeImage(idx)}
                                className="bg-[#7a3a2a] text-[#f5f1e6] text-[10px] tracking-[0.2em] px-2 py-1 inline-flex items-center gap-1">
                                <Trash2 className="w-3 h-3" strokeWidth={1.6} /> REMOVE
                              </button>
                            </div>
                            {isPrimary && (
                              <span className="absolute top-1.5 left-1.5 bg-[#1a1410] text-[#f5f1e6] text-[8px] tracking-[0.22em] px-1.5 py-0.5">PRIMARY</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mt-4 border border-dashed border-[#cdc2b1] py-10 text-center text-[#7a6a5a] text-sm">
                      No images yet. Use UPLOAD FROM DEVICE above or paste an image URL.
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">SHORT DESCRIPTION (2–3 lines shown above Add to Bag)</label>
                  <textarea
                    value={editing.short_description || ''}
                    onChange={(e) => setEditing({ ...editing, short_description: e.target.value.slice(0, 300) })}
                    rows={2}
                    maxLength={300}
                    placeholder="A short, benefit-led teaser (max 300 chars)…"
                    data-testid="admin-short-description"
                    className="mt-2 w-full bg-transparent border border-[#cdc2b1] focus:border-[#1a1410] p-3 text-sm focus:outline-none"
                  />
                  <p className="text-[10px] text-[#7a6a5a] mt-1">{(editing.short_description || '').length}/300 characters</p>
                </div>

                <div>
                  <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">DESCRIPTION</label>
                  <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    rows={3} className="mt-2 w-full bg-transparent border border-[#cdc2b1] focus:border-[#1a1410] p-3 text-sm focus:outline-none" />
                </div>

                <div>
                  <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">INGREDIENTS (optional)</label>
                  <textarea value={editing.ingredients || ''} onChange={(e) => setEditing({ ...editing, ingredients: e.target.value })}
                    rows={3} placeholder="Full INCI ingredient list…"
                    className="mt-2 w-full bg-transparent border border-[#cdc2b1] focus:border-[#1a1410] p-3 text-sm focus:outline-none" />
                  <p className="text-[10px] text-[#7a6a5a] mt-1">Leave blank to use the maison's default ingredient list.</p>
                </div>

                <Field
                  label="WEIGHT / NET QUANTITY (optional)"
                  value={editing.weight || ''}
                  onChange={(v) => setEditing({ ...editing, weight: v })}
                  required={false}
                />
                {err && <div className="p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}
                <div className="flex gap-3 pt-4">
                  <button type="submit" disabled={busy} className="flex-1 bg-[#1a1410] text-[#f5f1e6] py-3 text-[11px] tracking-[0.32em] hover:bg-black disabled:opacity-50">
                    {busy ? 'SAVING…' : 'SAVE'}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="px-6 py-3 text-[11px] tracking-[0.32em] border border-[#cdc2b1]">CANCEL</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

const Field = ({ label, value, onChange, type = 'text', step, required = true }) => (
  <div>
    <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">{label}</label>
    <input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} required={required}
      className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
  </div>
);

export default Admin;
